import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ControlCenterTable from "../../components/Table/ControlCenterTable";
import ControlCenterReportTable from "../../components/Table/ControlCenterReportTable";
import ReportingFormDrawerV2 from "../../components/Reporting/ReportingFormDrawerV2";
import {
	getControlCenterCounts,
	getControlCenterFilterOptions,
	getControlCenterReportsBySection,
} from "../../services/api-server/control_center";
import { getUsers } from "../../services/api-server/user";
import {
	ControlCenterFilters,
	ControlCenterReport,
	ControlCenterView,
} from "../../types/controlCenter.types";
import { TReportType } from "../../types/reporting.types";
import { formatDateTime } from "../../utils/date_formatter";
import { DEFAULT_REPORT_STATUSES } from "../../utils/reporting/reportStatusOptions";
import Emitter from "../../utils/emitter";

interface ControlCenterProps {
	searchValue?: string;
	sortOrder?: 1 | -1;
	onLastUpdated?: (dateTime: string) => void;
	showClosedReports?: boolean;
	viewMode?: ControlCenterView;
	selectedOperators?: string[];
	selectedProjects?: string[];
	selectedWells?: string[];
	selectedOperations?: string[];
	selectedReportStatuses?: string[];
}

type TaskSectionKey = "overdue" | "attention" | "on_track" | "completed";
type ReportSectionKey = "open_reports" | "closed_reports" | "draft_reports";
type SectionKey = TaskSectionKey | ReportSectionKey;

interface SectionData {
	items: ControlCenterReport[];
	total: number;
	offset: number;
	hasMore: boolean;
	isLoading: boolean;
}

const INITIAL_LIMIT = 7;
const LOAD_MORE_LIMIT = 10;

function getInitialLimitForSection(
	key: SectionKey,
	viewMode: ControlCenterView
): number | "all" {
	return viewMode === "report" && key === "open_reports"
		? "all"
		: INITIAL_LIMIT;
}

const TASK_SECTIONS: { key: TaskSectionKey; name: string }[] = [
	{ key: "overdue", name: "Overdue" },
	{ key: "attention", name: "Attention" },
	{ key: "on_track", name: "On track" },
	{ key: "completed", name: "Completed tasks" },
];

const REPORT_SECTIONS: { key: ReportSectionKey; name: string }[] = [
	{ key: "draft_reports", name: "Draft reports" },
	{ key: "open_reports", name: "Open reports" },
	{ key: "closed_reports", name: "Closed reports" },
];

function getSectionsForView(viewMode: ControlCenterView) {
	return viewMode === "report" ? REPORT_SECTIONS : TASK_SECTIONS;
}

function buildEmptySectionData(
	viewMode: ControlCenterView
): Record<string, SectionData> {
	const sections = getSectionsForView(viewMode);
	const empty: Record<string, SectionData> = {};
	sections.forEach(({ key }) => {
		empty[key] = {
			items: [],
			total: 0,
			offset: 0,
			hasMore: false,
			isLoading: false,
		};
	});
	return empty;
}

function collectAllEmails(items: ControlCenterReport[]): string[] {
	if (!Array.isArray(items)) return [];
	const emails = new Set<string>();
	items.forEach((item) => {
		if (item.task_assigned_to) emails.add(item.task_assigned_to.toLowerCase());
		if (item.primary_author) emails.add(item.primary_author.toLowerCase());
		if (item.author) emails.add(item.author.toLowerCase());
		if (item.reviewer) emails.add(item.reviewer.toLowerCase());
		const actions = item.findings || item.follow_up_actions || [];
		actions.forEach((action) => {
			if (action.responsible) emails.add(action.responsible.toLowerCase());
		});
	});
	return Array.from(emails);
}

interface User {
	email?: string;
	name?: string;
}

async function fetchEmailToNameMap(
	emails: string[]
): Promise<Record<string, string>> {
	if (!emails?.length) return {};
	const users = (await getUsers(emails)) as User[] | null;
	const map: Record<string, string> = {};
	(users ?? []).forEach((u: User) => {
		if (u?.email) {
			map[u.email.toLowerCase()] = u?.name ?? u?.email;
		}
	});
	return map;
}

const ControlCenter = ({
	onLastUpdated,
	showClosedReports = false,
	viewMode = "report",
	selectedOperators = [],
	selectedProjects = [],
	selectedWells = [],
	selectedOperations = [],
	selectedReportStatuses = [],
}: ControlCenterProps) => {
	const [selectedReport, setSelectedReport] =
		useState<ControlCenterReport | null>(null);
	const [openFormDrawer, setOpenFormDrawer] = useState(false);
	const [formReportType, setFormReportType] = useState<TReportType | null>(
		null
	);
	const [formActiveId, setFormActiveId] = useState<string | undefined>(
		undefined
	);

	const HUB_STATUS_TO_COMBINED: Record<string, string[]> = {
		draft: ["draft"],
		in_review: ["in_review"],
		in_progress: ["in_progress", "changes_required", "revision_required"],
		final_review: ["final_review"],
		ready_to_close: ["ready_to_close"],
		closed: ["closed_approved", "closed_rejected"],
	};

	function mapHubStatusesToCombined(statuses: string[]): string[] {
		return Array.from(
			new Set(statuses.flatMap((s) => HUB_STATUS_TO_COMBINED[s] || []))
		);
	}

	// Filter state
	const [filters, setFilters] = useState<ControlCenterFilters>({});
	const [isRefetching, setIsRefetching] = useState(false);

	// Section data state
	const [sectionData, setSectionData] = useState<Record<string, SectionData>>(
		() => buildEmptySectionData(viewMode)
	);
	const [sectionDataView, setSectionDataView] =
		useState<ControlCenterView>(viewMode);

	const sections = useMemo(() => getSectionsForView(viewMode), [viewMode]);

	// Fetch counts
	const { data: countsData, isLoading: isLoadingCounts } = useQuery({
		queryKey: ["control_center_counts", viewMode],
		queryFn: async () => {
			const res = await getControlCenterCounts(viewMode);
			const formattedDate = formatDateTime(new Date().toISOString());
			onLastUpdated?.(formattedDate);
			return res;
		},
		refetchOnMount: "always",
		refetchOnWindowFocus: true,
		staleTime: 0,
	});

	// Fetch filter options (all unique report_types and combined_statuses from full dataset)
	const { data: filterOptionsData } = useQuery({
		queryKey: ["control_center_filter_options", viewMode],
		queryFn: () => getControlCenterFilterOptions(viewMode),
		refetchOnMount: "always",
		refetchOnWindowFocus: true,
		staleTime: 0,
	});

	// Update combined_statuses filter based on showClosedReports toggle (task view only)
	useEffect(() => {
		if (viewMode !== "task") return;

		const closedStatuses = ["closed_approved", "closed_rejected"];

		if (!showClosedReports) {
			const nonClosed = (
				filterOptionsData?.combined_statuses || DEFAULT_REPORT_STATUSES
			).filter((s) => !closedStatuses.includes(s));
			setFilters((prev) => ({
				...prev,
				combined_statuses: nonClosed,
			}));
		} else {
			setFilters((prev) => {
				const newFilters = { ...prev };
				delete newFilters.combined_statuses;
				return newFilters;
			});
		}
	}, [showClosedReports, viewMode]);

	// Reset filters and section data when viewMode changes
	useEffect(() => {
		setSectionData(buildEmptySectionData(viewMode));
		setSectionDataView(viewMode);
		setFilters({});
	}, [viewMode]);

	// Sync incoming prop-driven filters. Placed after the viewMode reset effect
	// so that Hub-style filter selections are restored after a view switch.
	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			tenants: selectedOperators.length > 0 ? selectedOperators : undefined,
			projects: selectedProjects.length > 0 ? selectedProjects : undefined,
			wells: selectedWells.length > 0 ? selectedWells : undefined,
			operations:
				selectedOperations.length > 0 ? selectedOperations : undefined,
			combined_statuses:
				selectedReportStatuses.length > 0
					? mapHubStatusesToCombined(selectedReportStatuses)
					: undefined,
		}));
	}, [
		selectedOperators,
		selectedProjects,
		selectedWells,
		selectedOperations,
		selectedReportStatuses,
	]);

	// Fetch initial data for all sections when counts are available
	// Note: filters are intentionally omitted from deps to avoid double-fetching
	// since filter changes are handled separately by the debounced effect below
	useEffect(() => {
		if (countsData?.counts) {
			const fetchInitialData = async () => {
				const promises = sections.map(async ({ key }) => {
					const count =
						countsData.counts[key as keyof typeof countsData.counts];
					if (count && count > 0) {
						try {
							const res = await getControlCenterReportsBySection(
								key,
								getInitialLimitForSection(key, viewMode),
								0,
								{ ...filters, view: viewMode }
							);
							return {
								key,
								data: {
									items: res.reports,
									total: res.total,
									offset: res.reports.length,
									hasMore: res.hasMore,
									isLoading: false,
								},
							};
						} catch (error) {
							console.error(`Failed to fetch ${key}:`, error);
							return {
								key,
								data: {
									items: [],
									total: count,
									offset: 0,
									hasMore: count > 0,
									isLoading: false,
								},
							};
						}
					} else {
						return {
							key,
							data: {
								items: [],
								total: 0,
								offset: 0,
								hasMore: false,
								isLoading: false,
							},
						};
					}
				});

				const results = await Promise.all(promises);
				setSectionData((prev) => {
					const newSectionData = { ...prev };
					results.forEach(({ key, data }) => {
						newSectionData[key] = data;
					});
					return newSectionData;
				});
			};

			fetchInitialData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countsData, viewMode, sections]);

	// Handle load more for a section
	const handleLoadMore = useCallback(
		async (sectionKey: SectionKey) => {
			const section = sectionData[sectionKey];
			if (section.isLoading || !section.hasMore) return;

			// Emit loading event for this section
			Emitter.emit("loading", {
				name: `control-center-load-more-${sectionKey}`,
				isLoading: true,
			});

			// Update local loading state
			setSectionData((prev) => ({
				...prev,
				[sectionKey]: { ...prev[sectionKey], isLoading: true },
			}));

			try {
				const res = await getControlCenterReportsBySection(
					sectionKey,
					LOAD_MORE_LIMIT,
					section.offset,
					{ ...filters, view: viewMode }
				);

				setSectionData((prev) => ({
					...prev,
					[sectionKey]: {
						items: [...prev[sectionKey].items, ...res.reports],
						total: res.total,
						offset: prev[sectionKey].offset + res.reports.length,
						hasMore: res.hasMore,
						isLoading: false,
					},
				}));
			} catch (error) {
				console.error(`Failed to load more ${sectionKey}:`, error);
				setSectionData((prev) => ({
					...prev,
					[sectionKey]: { ...prev[sectionKey], isLoading: false },
				}));
			} finally {
				// Turn off loading emitter
				Emitter.emit("loading", {
					name: `control-center-load-more-${sectionKey}`,
					isLoading: false,
				});
			}
		},
		[sectionData, filters, viewMode]
	);

	// Simple filter update - no side effects
	const updateFilters = useCallback(
		(newFilters: Partial<ControlCenterFilters>) => {
			setFilters((prev) => ({ ...prev, ...newFilters }));
		},
		[]
	);

	// Debounced refetch when filters change
	useEffect(() => {
		let cancelled = false;

		const timer = setTimeout(() => {
			if (cancelled) return;

			setIsRefetching(true);

			const fetchAllSections = async () => {
				const promises = sections.map(async ({ key }) => {
					try {
						const res = await getControlCenterReportsBySection(
							key,
							getInitialLimitForSection(key, viewMode),
							0,
							{ ...filters, view: viewMode }
						);
						return {
							key,
							data: {
								items: res.reports,
								total: res.total,
								offset: res.reports.length,
								hasMore: res.hasMore,
								isLoading: false,
							},
						};
					} catch (error) {
						console.error(`Failed to refetch ${key}:`, error);
						return {
							key,
							data: {
								items: [],
								total: 0,
								offset: 0,
								hasMore: false,
								isLoading: false,
							},
						};
					}
				});

				const results = await Promise.all(promises);

				if (cancelled) return;

				setSectionData((prev) => {
					const newSectionData = { ...prev };
					results.forEach(({ key, data }) => {
						newSectionData[key] = data;
					});
					return newSectionData;
				});

				if (!cancelled) {
					setIsRefetching(false);
				}
			};

			fetchAllSections();
		}, 300);

		return () => {
			clearTimeout(timer);
			cancelled = true;
		};
	}, [filters, viewMode, sections]);

	// Listen for explicit refresh requests from status update modal
	useEffect(() => {
		const handleControlCenterUpdated = () => {
			const refetchAll = async () => {
				setIsRefetching(true);
				const promises = sections.map(async ({ key }) => {
					try {
						const res = await getControlCenterReportsBySection(
							key,
							getInitialLimitForSection(key, viewMode),
							0,
							{ ...filters, view: viewMode }
						);
						return {
							key,
							data: {
								items: res.reports,
								total: res.total,
								offset: res.reports.length,
								hasMore: res.hasMore,
								isLoading: false,
							},
						};
					} catch (error) {
						console.error(`Failed to refetch ${key}:`, error);
						return {
							key,
							data: {
								items: [],
								total: 0,
								offset: 0,
								hasMore: false,
								isLoading: false,
							},
						};
					}
				});

				const results = await Promise.all(promises);

				setSectionData((prev) => {
					const newSectionData = { ...prev };
					results.forEach(({ key, data }) => {
						newSectionData[key] = data;
					});
					return newSectionData;
				});

				setIsRefetching(false);
			};

			refetchAll();
		};

		Emitter.on("controlCenterUpdated", handleControlCenterUpdated);
		return () => {
			Emitter.off("controlCenterUpdated", handleControlCenterUpdated);
		};
	}, [filters, viewMode, sections]);

	// Combine all items for email collection
	const allItems = useMemo(() => {
		return Object.values(sectionData).flatMap((section) => section.items);
	}, [sectionData]);

	// Collect all emails
	const allEmails = useMemo(() => collectAllEmails(allItems), [allItems]);

	// Fetch user names for all emails
	const { data: emailToNameMap } = useQuery({
		queryKey: ["control_center_user_names", allEmails, viewMode],
		queryFn: () => fetchEmailToNameMap(allEmails),
		enabled: allEmails.length > 0,
		refetchOnWindowFocus: true,
		staleTime: 0,
	});

	const handleRowClick = (report: ControlCenterReport) => {
		setSelectedReport(report);
		setFormReportType(report.report_type as TReportType);
		setFormActiveId(report._id);
		setOpenFormDrawer(true);
	};

	// Convert section data to the format expected by tables
	const sectionCounts = useMemo(() => {
		const counts: Record<string, number> = {};
		sections.forEach(({ key }) => {
			counts[key] = sectionData[key]?.total ?? 0;
		});
		return counts;
	}, [sectionData, sections]);

	const isReady = sectionDataView === viewMode;

	return (
		<>
			{isReady && viewMode === "task" && (
				<ControlCenterTable
					sectionData={sectionData as Record<TaskSectionKey, SectionData>}
					sectionCounts={sectionCounts as Record<TaskSectionKey, number>}
					loading={isLoadingCounts}
					isRefetching={isRefetching}
					onRowClick={handleRowClick}
					onLoadMore={handleLoadMore}
					emailToNameMap={emailToNameMap ?? {}}
					onFiltersChange={updateFilters}
					filters={filters}
					filterOptions={filterOptionsData}
				/>
			)}
			{isReady && viewMode === "report" && (
				<ControlCenterReportTable
					sectionData={sectionData as Record<ReportSectionKey, SectionData>}
					sectionCounts={sectionCounts as Record<ReportSectionKey, number>}
					loading={isLoadingCounts}
					isRefetching={isRefetching}
					onRowClick={handleRowClick}
					onLoadMore={handleLoadMore}
					emailToNameMap={emailToNameMap ?? {}}
					onFiltersChange={updateFilters}
					filters={filters}
					filterOptions={filterOptionsData}
				/>
			)}

			<ReportingFormDrawerV2
				isOpen={openFormDrawer}
				reportType={formReportType as TReportType}
				activeId={formActiveId}
				taskId={selectedReport?.task_id}
				showUpdateStatus={true}
				onClose={() => {
					setOpenFormDrawer(false);
					setFormActiveId(undefined);
					setFormReportType(null);
					setSelectedReport(null);
				}}
			/>
		</>
	);
};

export default ControlCenter;
